<script>
    import { onMount } from 'svelte';
    const API_BASE_URL = 'https://vision-api.radiclellp.in';

    let video;
    let canvas;
    let results = '';

    let visible = false;
    let status = '';
    let message = '';
    let predictedClass = '';
    let confidence = 0;
    let averageConfidence = 0;
    let voteRatio = 0;

    let title = '';
    let information = '';
    let frontUrl = '';
    let backUrl = '';
    let position = '';
    let year = '';

    let top3 = [];
    let framesAnalyzed = 0;
    let validFrames = 0;
    let rejectedFrames = 0;
    let loading = false;
    let showDebug = false;

    function startVideo() {
        navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
            .then(stream => {
                video.srcObject = stream;
                video.play();
            })
            .catch(err => {
                console.error('Error accessing webcam: ', err);
            });
    }

    function drawFrameToBlob() {
        return new Promise((resolve, reject) => {
            const context = canvas.getContext('2d');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(blob => {
                if (!blob) {
                    reject(new Error('Failed to capture frame'));
                    return;
                }
                resolve(blob);
            }, 'image/png');
        });
    }

    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    function applyResponse(data) {
        results = JSON.stringify(data, null, 2);

        status = data.status || '';
        message = data.message || '';
        predictedClass = data.predicted_class || '';
        confidence = data.confidence || 0;
        averageConfidence = data.average_confidence || 0;
        voteRatio = data.vote_ratio || 0;

        top3 = data.top3 || [];
        framesAnalyzed = data.frames_analyzed || 0;
        validFrames = data.valid_frames || 0;
        rejectedFrames = data.rejected_frames || 0;

        const metadata = data.metadata || {};
        title = metadata.title || '';
        information = metadata.information || '';
        frontUrl = normalizeImageUrl(metadata.front_url || '');
        backUrl = normalizeImageUrl(metadata.back_url || '');
        position = metadata.position || '';
        year = metadata.year || '';

        visible = true;
    }

    async function captureImage() {
        loading = true;
        message = 'Capturing image. Keep the medal inside the frame.';
        const context = canvas.getContext('2d');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        try {
            const blob = await drawFrameToBlob();
            const formData = new FormData();
            formData.append('image_bytes', blob, 'capture.png');

            const response = await fetch(`${API_BASE_URL}/detect/`, {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            applyResponse(data);
        } catch (error) {
            console.error('Error:', error);
            results = 'Error: ' + error;
        } finally {
            loading = false;
        }
    }

    function normalizeImageUrl(url) {
        if (!url) return '';
        if (url.startsWith('http://')) {
            return `https://${url.slice('http://'.length)}`;
        }
        return url;
    }

    async function captureBatch() {
        loading = true;
        message = 'Capturing multiple images. Keep the medal inside the frame.';
        try {
            const formData = new FormData();
            const frameCount = 5;
            for (let i = 0; i < frameCount; i += 1) {
                const blob = await drawFrameToBlob();
                formData.append('images', blob, `capture-${i}.png`);
                if (i < frameCount - 1) {
                    await sleep(600);
                }
            }

            const response = await fetch(`${API_BASE_URL}/detect/batch`, {
                method: 'POST',
                body: formData
            });
            const data = await response.json();
            applyResponse(data);
        } catch (error) {
            console.error('Error:', error);
            results = 'Error: ' + error;
        } finally {
            loading = false;
        }
    }

    onMount(() => {
        startVideo();
    });
</script>

<style>
    :global(body) {
        margin: 0;
        font-family: 'Roboto Mono', monospace;
        background:
            radial-gradient(circle at 18% 18%, #205799 0%, transparent 45%),
            radial-gradient(circle at 86% 14%, #4a2f7f 0%, transparent 40%),
            linear-gradient(135deg, #0a1222 0%, #132340 45%, #192f53 100%);
        color: #e8f1ff;
        padding: 16px;
        box-sizing: border-box;
    }

    :global(*),
    :global(*::before),
    :global(*::after) {
        box-sizing: inherit;
    }

    main {
        width: 100%;
        max-width: 1100px;
        margin: 0 auto;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
        padding: 18px 0 30px;
        box-sizing: border-box;
    }

    .hero,
    .panel,
    .image-card,
    .debug-wrap {
        width: min(980px, 100%);
        margin: 0 auto;
        background: rgba(9, 18, 35, 0.76);
        border: 1px solid rgba(133, 190, 255, 0.34);
        border-radius: 22px;
        padding: 18px;
        box-shadow: 0 20px 40px rgba(3, 9, 22, 0.5);
        box-sizing: border-box;
    }

    .hero {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    h1 {
        margin: 0 0 10px;
        font-size: clamp(20px, 2.4vw, 32px);
        letter-spacing: 1px;
        color: #c8e3ff;
    }

    h3 {
        margin: 0;
        color: #c8e3ff;
        letter-spacing: 0.5px;
    }

    .sub {
        margin: 0;
        font-size: 12px;
        color: #b2c7e6;
    }

    .hero .sub {
        margin-bottom: 12px;
    }

    video,
    canvas,
    img {
        width: min(78vw, 560px);
        height: min(78vw, 560px);
        object-fit: cover;
        border-radius: 16px;
        border: 1px solid rgba(133, 190, 255, 0.45);
    }

    .actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-top: 14px;
        justify-content: center;
    }

    button {
        border: 0;
        border-radius: 12px;
        cursor: pointer;
        font-family: 'Roboto Mono', monospace;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.3px;
        transition: transform 120ms ease, filter 120ms ease;
    }

    button:hover:not(:disabled) {
        transform: translateY(-1px);
        filter: brightness(1.04);
    }

    button:disabled {
        opacity: 0.75;
        cursor: wait;
    }

    .btn-primary {
        padding: 13px 18px;
        background: linear-gradient(135deg, #16b4ff 0%, #5d82ff 100%);
        color: #06142b;
    }

    .btn-secondary {
        padding: 13px 18px;
        background: linear-gradient(135deg, #67e8a9 0%, #47b7ff 100%);
        color: #062321;
    }

    .btn-ar-link {
        display: inline-block;
        margin-top: 10px;
        padding: 11px 16px;
        border-radius: 12px;
        border: 1px solid rgba(148, 196, 255, 0.55);
        background: rgba(27, 41, 67, 0.9);
        color: #d9e9ff;
        text-decoration: none;
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.3px;
        transition: transform 120ms ease, filter 120ms ease;
    }

    .btn-ar-link:hover {
        transform: translateY(-1px);
        filter: brightness(1.04);
    }

    .btn-debug {
        margin-top: 2px;
        padding: 10px 14px;
        border: 1px solid rgba(148, 196, 255, 0.55);
        background: rgba(27, 41, 67, 0.9);
        color: #d9e9ff;
    }

    .status-chip {
        margin-top: 13px;
        display: inline-block;
        padding: 8px 12px;
        border-radius: 999px;
        border: 1px solid rgba(155, 202, 255, 0.42);
        background: rgba(30, 58, 94, 0.6);
        color: #d4e9ff;
        font-size: 12px;
        max-width: 100%;
        white-space: normal;
        overflow-wrap: anywhere;
    }

    .meta-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 10px;
        margin-top: 10px;
    }

    .meta-item {
        border: 1px solid rgba(134, 190, 255, 0.3);
        border-radius: 12px;
        padding: 10px;
        background: rgba(18, 29, 52, 0.6);
    }

    .meta-label {
        display: block;
        font-size: 11px;
        color: #90b1db;
        margin-bottom: 5px;
    }

    .meta-value {
        font-size: 13px;
        color: #e4efff;
    }

    .info {
        margin: 10px 0 0;
        border: 1px solid rgba(133, 190, 255, 0.25);
        border-radius: 12px;
        padding: 12px;
        background: rgba(17, 29, 53, 0.58);
        color: #d5e8ff;
        line-height: 1.55;
        font-size: 13px;
    }

    .image-row {
        margin-top: 12px;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
    }

    .image-pane {
        border: 1px solid rgba(133, 190, 255, 0.3);
        border-radius: 14px;
        padding: 10px;
        background: rgba(16, 25, 46, 0.7);
    }

    .image-title {
        margin: 0 0 8px;
        font-size: 12px;
        color: #c6e1ff;
    }

    img {
        width: 100%;
        height: min(40vw, 320px);
    }

    .top3 {
        margin-top: 12px;
        border: 1px solid rgba(133, 190, 255, 0.24);
        border-radius: 12px;
        padding: 10px 10px 10px 26px;
        background: rgba(17, 29, 53, 0.56);
        font-size: 13px;
    }

    .frame-info {
        margin-top: 10px;
        color: #b7cae8;
        font-size: 12px;
    }

    .details-footer {
        margin-top: 12px;
        border: 1px solid rgba(133, 190, 255, 0.24);
        border-radius: 12px;
        padding: 10px;
        background: rgba(17, 29, 53, 0.5);
    }

    pre {
        margin-top: 12px;
        max-width: 100%;
        overflow: auto;
        background: rgba(3, 11, 23, 0.9);
        color: #b9e2ff;
        border: 1px solid rgba(133, 190, 255, 0.28);
        border-radius: 12px;
        padding: 12px;
        line-height: 1.45;
        font-size: 12px;
    }

    @media (max-width: 860px) {
        :global(body) {
            padding: 12px;
        }

        .meta-grid {
            grid-template-columns: 1fr;
        }

        .image-row {
            grid-template-columns: 1fr;
        }
    }
</style>

<svelte:head>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous" />
    <link href="https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
    <title>Radicle Visual Assistant - Radicle LLP</title>
</svelte:head>

<main>
    <section class="hero">
        <h1>Radicle LLP Visual Assistant</h1>
        <p class="sub">Align the medal inside the frame and scan.</p>
        <!-- svelte-ignore a11y-media-has-caption -->
        <video bind:this={video} autoplay></video>
        <canvas bind:this={canvas} style="display:none;"></canvas>

        <div class="actions">
            <button class="btn-primary" on:click={captureImage} disabled={loading}>Check Quickly</button>
            <button class="btn-secondary" on:click={captureBatch} disabled={loading}>Check (Takes More Time)</button>
        </div>
        <a class="btn-ar-link" href="/ar.html">Open AR Experience</a>

        {#if status}
            <div class="status-chip">
                Status: {status}
            </div>
        {/if}

        {#if !loading && visible && message}
            <p class="sub" style="margin-top: 10px;">{message}</p>
        {/if}

        {#if loading}
            <p class="sub" style="margin-top: 10px;">{message}</p>
        {/if}
    </section>

    {#if visible}
        <section class="panel">
            <h3>Details</h3>
            <div class="meta-grid">
                <div class="meta-item">
                    <span class="meta-label">Title</span>
                    <span class="meta-value">{title || '-'}</span>
                </div>
                <div class="meta-item">
                    <span class="meta-label">Position</span>
                    <span class="meta-value">{position || '-'}</span>
                </div>
                <div class="meta-item">
                    <span class="meta-label">Year</span>
                    <span class="meta-value">{year || '-'}</span>
                </div>
            </div>
            <p class="info">{information || 'No information available.'}</p>

            <div class="details-footer">
                <p class="frame-info">Predicted Class: {predictedClass || '-'}</p>
                <p class="frame-info">Confidence: {confidence} | Average: {averageConfidence} | Vote Ratio: {voteRatio}</p>
                <p class="frame-info">Frames: {validFrames}/{framesAnalyzed} valid ({rejectedFrames} rejected)</p>
                <p class="frame-info">{message}</p>

                <ul class="top3">
                    {#each top3 as item}
                        <li>{item.class_name} | conf: {item.confidence} | sim: {item.similarity_score}</li>
                    {/each}
                </ul>
            </div>
        </section>

        <section class="image-card">
            <h3>Front / Back Reference</h3>
            <div class="image-row">
                <div class="image-pane">
                    <p class="image-title">Front</p>
                    {#if frontUrl}
                        <img src={frontUrl} alt="front side medal" />
                    {:else}
                        <p class="sub">Front image unavailable.</p>
                    {/if}
                </div>
                <div class="image-pane">
                    <p class="image-title">Back</p>
                    {#if backUrl}
                        <img src={backUrl} alt="back side medal" />
                    {:else}
                        <p class="sub">Back image unavailable.</p>
                    {/if}
                </div>
            </div>
        </section>

        <section class="debug-wrap">
            <button class="btn-debug" on:click={() => (showDebug = !showDebug)}>
                {showDebug ? 'Hide Debug JSON' : 'Show Debug JSON'}
            </button>
            {#if showDebug}
                <pre>{results}</pre>
            {/if}
        </section>
    {/if}
</main>
